import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { CheckoutService } from 'src/app/pages/main.page/services/checkout.service';
import { SelectedAttributeService } from 'src/app/pages/main.page/services/selected-attribute.service';
import { PriceProviderService } from 'src/app/providers/price/price.provider.service';
import { assert } from 'src/app/utils/assert.util';
import { ConfiguredItemService } from '../configured-item/configured-item.service';
import { PricingObject } from 'src/app/interfaces/pricing-object';
import { Price } from 'src/app/interfaces/Price';
import { LoadingService } from '../loading/loading.service';
import { engravingFeeFor } from 'src/app/utils/engraving.util';
import { roundRetailerPrice } from 'src/app/utils/retail-price.util';

@Injectable({
  providedIn: 'root'
})
export class PriceService {
  private loadingService = inject(LoadingService);
  private configuredItemService = inject(ConfiguredItemService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private checkoutService = inject(CheckoutService);
  private priceProvider = inject(PriceProviderService);
  private wholesalePriceAdjustedForFingerSize = signal<number | undefined>(undefined);
  wholesalePrice = signal<Price | undefined>(undefined);
  isPricedItemQuarterSized = signal<boolean>(false);
  retailerOnlyPrice = signal<number | undefined>(undefined);
  shouldUseCustomMarkups = signal<boolean>(false);
  markupCustomPrecious = signal<number | null>(null);
  markupCustomContemporary = signal<number | null>(null);

  // In-flight dedupe for concurrent update() calls (S10b-dedupe). Calls with an
  // equal key (style + size + isSharing) share one underlying provider promise
  // instead of issuing duplicate requests. The entry is removed once settled
  // (success or rejection), so a later call always re-fetches and a call after
  // a rejection retries rather than replaying the failure.
  private inFlightUpdates = new Map<string, Promise<void>>();

   wholesalePriceToShow = computed(() => {
     const wholesalePrice = this.wholesalePriceAdjustedForFingerSize();
     const configuredItem = this.selectedAttributeService.configuredItem();
     return wholesalePrice ? wholesalePrice + engravingFeeFor(configuredItem) : undefined;
   });
  retailPrice = computed(() => {
    const wholesalePrice = this.wholesalePrice();
    const retailerOnlyPrice = this.retailerOnlyPrice();

    if (retailerOnlyPrice) {
      return this.computeRetailerPrice(retailerOnlyPrice, this.selectedAttributeService.configuredItem());
    }

    if (wholesalePrice) {
      return this.computeRetailPriceFromWholesale(wholesalePrice, untracked(() => this.shouldUseCustomMarkups()));
    }

    return undefined;
  });

  constructor() {
    (async () => {
      const { customerToken, customer: customerID } = this.checkoutService.b2bData;
      if (customerToken) {
        if (!this.checkoutService.isRetailerOnly()) {
          this.loadingService.load('PriceService::GetItemData');
          const { p, c } = await this.priceProvider.getCustomMarkups(customerToken);
          this.markupCustomPrecious.set(p);
          this.markupCustomContemporary.set(c);
          this.loadingService.done('PriceService::GetItemData');
        }
      } else {
        if (!this.checkoutService.isRetailerOnly()) {
          assert(customerID, 'No customer ID');
          this.loadingService.load('PriceService::GetItemData');
          const { p, c } = await this.priceProvider.getCustomMarkupsByHALOID(customerID);
          this.markupCustomPrecious.set(p);
          this.markupCustomContemporary.set(c);
          this.loadingService.done('PriceService::GetItemData');
        }
      }
    })();
  }

   computeRetailPriceFromWholesale(wholesalePrice: Price, shouldUseCustomMarkups: boolean) {
     const configuredItem = this.selectedAttributeService.configuredItem();
     const shouldUseMarkupP = [configuredItem.primaryMaterial?.shortName, configuredItem.secondaryMaterial?.shortName].reduce((flag, material) => flag || !!material?.match(/\d+[wWyYrR]|PT/), false);
     const markupP = !shouldUseCustomMarkups ? this.checkoutService.b2bData.markupP : untracked(() => this.markupCustomPrecious());
     const markupC = !shouldUseCustomMarkups ? this.checkoutService.b2bData.markupC : untracked(() => this.markupCustomContemporary());
     const markedUpWholesalePrice = wholesalePrice ? wholesalePrice.valueBeforeFee * (shouldUseMarkupP ? (markupP ?? 2.45) : (markupC ?? 3)) + engravingFeeFor(configuredItem) : undefined;
     if (markedUpWholesalePrice) {
       return markedUpWholesalePrice + wholesalePrice.flatFee;
     }

     return undefined;
   }

  // A retailer's price comes from the API without the engraving fee, because the price endpoints
  // are never told about the engraving. Add it and round exactly as ItemsService.Get does, since
  // that is what the item is billed at. (ADO #10114)
  computeRetailerPrice(retailerPrice: number, configuredItem: Pick<ConfiguredItem, 'engravingText' | 'engravingType'>) {
    return roundRetailerPrice(retailerPrice + engravingFeeFor(configuredItem));
  }

  clear(isSharing: boolean = false) {
    this.checkoutService.price = null;
    this.wholesalePrice.set(undefined);
    this.wholesalePriceAdjustedForFingerSize.set(undefined);
    if (this.checkoutService.isRetailerOnly() || isSharing) {
      this.retailerOnlyPrice.set(undefined);
    }
  }

  async update(pricingObject: PricingObject, configuredItem: ConfiguredItem, isSharing: boolean = false) {
    const key = `${pricingObject.style}|${pricingObject.size.value}|${isSharing}`;
    const inFlight = this.inFlightUpdates.get(key);
    if (inFlight) {
      return inFlight;
    }
    const shared = this.runUpdate(pricingObject, configuredItem, isSharing).finally(() => {
      if (this.inFlightUpdates.get(key) === shared) {
        this.inFlightUpdates.delete(key);
      }
    });
    this.inFlightUpdates.set(key, shared);
    return shared;
  }

  private async runUpdate(pricingObject: PricingObject, configuredItem: ConfiguredItem, isSharing: boolean = false) {
    const isHaloStyle = !this.configuredItemService.isCustomSKU(pricingObject.style) && !this.configuredItemService.isCustomStyle(pricingObject.style);
    if (isHaloStyle) {
      try {
        this.shouldUseCustomMarkups.set(false);
        await this.updateWithHaloStyle(pricingObject.style, pricingObject.size.value, isSharing);
      } catch {
        this.shouldUseCustomMarkups.set(true);
        await this.updateWithConfiguredItem(configuredItem, isSharing);
      }
    } else {
      this.shouldUseCustomMarkups.set(true);
      await this.updateWithConfiguredItem(configuredItem, isSharing);
    }
  }

  async updateWithConfiguredItem(configuredItem: ConfiguredItem, isSharing: boolean = false) {
    try {
      assert(configuredItem.size, 'Size was null');
      const price = await this.getPrice(configuredItem, configuredItem.size.value);
      this.checkoutService.price = price.valueBeforeFee;
      this.isPricedItemQuarterSized.set(price.isQuarterSize);
      if (this.checkoutService.isRetailerOnly() || isSharing) {
        this.retailerOnlyPrice.set(price.value);
      } else {
        this.wholesalePrice.set(price);
        this.wholesalePriceAdjustedForFingerSize.set(price.value);
      }
    } catch (e) {
      console.error(e);
      this.checkoutService.price = null;
      this.isPricedItemQuarterSized.set(false);
      this.wholesalePrice.set(undefined);
      this.wholesalePriceAdjustedForFingerSize.set(undefined);
      if (this.checkoutService.isRetailerOnly() || isSharing) {
        this.retailerOnlyPrice.set(undefined);
      }
    }
  }

  async getPrice(configuredItem: ConfiguredItem, size: number) {
    const style = this.configuredItemService.getNewStyleFromSelectedAttributes(configuredItem);
    const pc = this.checkoutService.b2bData.pc;
    if (this.checkoutService.isRetailerOnly()) {
      const retailerIframeID = this.checkoutService.retailerIframeID;
      assert(retailerIframeID, 'No retailer iframe ID');
      const price = await this.priceProvider.getRetailerPrice(retailerIframeID, style, size);
      return price;
    }
    const price = await this.priceProvider.getPrice(pc, style, size);
    return price;
  }

  async updateWithHaloStyle(haloStyle: string, size: number, isSharing: boolean = false) {
    try {
      const price = await this.getPriceWithHaloStyle(haloStyle, size, isSharing);
      this.checkoutService.price = price.valueBeforeFee;
      this.isPricedItemQuarterSized.set(price.isQuarterSize);
      if (this.checkoutService.isRetailerOnly() || isSharing) {
        this.retailerOnlyPrice.set(price.value);
      } else {
        this.wholesalePrice.set(price);
        this.wholesalePriceAdjustedForFingerSize.set(price.value);
      }
    } catch (e) {
      console.error(e);
      this.checkoutService.price = null;
      this.isPricedItemQuarterSized.set(false);
      this.wholesalePrice.set(undefined);
      this.wholesalePriceAdjustedForFingerSize.set(undefined);
      if (this.checkoutService.isRetailerOnly() || isSharing) {
        this.retailerOnlyPrice.set(undefined);
      }
    }
  }

  async getPriceWithHaloStyle(haloStyle: string, size: number, isSharing: boolean = false) {
    if (this.checkoutService.isRetailerOnly()) {
      const retailerIframeID = this.checkoutService.retailerIframeID;
      assert(retailerIframeID, 'No retailer iframe ID');
      const price = await this.priceProvider.getRetailerPriceWithHALOItemNumber(retailerIframeID, haloStyle, size);
      return price;
    }
    if (isSharing) {
      const retailerIframeID = this.checkoutService.retailerIframeID;
      assert(retailerIframeID, 'No retailer iframe ID');
      const price = await this.priceProvider.getRetailerPriceWithHALOItemNumber(retailerIframeID, haloStyle, size);
      return price;
    }
    const ringBuilderID = this.checkoutService.ringBuilderID;
    assert(ringBuilderID, 'No ring builder ID');
    const price = await this.priceProvider.getPriceWithHALOItemNumberByRingBuilderID(ringBuilderID, haloStyle, size);
    return price;
  }
}
